/**
 * PageLayout – Wrapper for pages with Sidebar + TopBar.
 * Enforces ml: sidebarWidth, maxWidth: contentMaxWidth, consistent padding.
 * See docs/LAYOUT.md for maxWidth="none", BillingLayout, and ReportsLayout conventions.
 *
 * Props:
 *   onOptionSelect     fn         Forwarded to Sidebar for navigation callbacks.
 *   maxWidth           number|string  Content max-width (default: tokens.contentMaxWidth).
 *   padding            number     MUI spacing for main padding (default: tokens.pageSpacing).
 *   chromeSx           object     Extra sx merged into top chrome (legacy, ignored).
 *   sx                 object     Extra sx for the main content Box.
 *   hideGlobalSearch   boolean    Unused (kept for API compat).
 *   children                       Page content.
 */
import React from 'react';
import { Box } from '@mui/material';
import Sidebar from '../Sidebar';
import TopBar, { TOP_BAR_HEIGHT } from './TopBar';
import { tokens } from '../../theme';

const PageLayout = ({
  children,
  onOptionSelect = () => {},
  maxWidth = tokens.contentMaxWidth,
  padding = tokens.pageSpacing,
  chromeSx = {},
  sx = {},
  hideGlobalSearch = false,
  ...rest
}) => {
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Sidebar onOptionSelect={onOptionSelect} />
      <Box
        sx={{
          flex: 1,
          ml: `${tokens.sidebarWidth}px`,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TopBar />
        {/* Spacer that occupies the same height as the fixed TopBar */}
        <Box sx={{ height: TOP_BAR_HEIGHT, flexShrink: 0 }} />
        <Box
          component="main"
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            p: padding,
            maxWidth,
            mx: 'auto',
            width: '100%',
            overflow: 'auto',
            ...sx,
          }}
          {...rest}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default PageLayout;
